/* original source: https://ui.shadcn.com/docs/components/select */

import { Select as SelectPrimitive } from 'radix-ui'
import { cn, focusRingClassName } from '@joyful/utils'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Button, RotatingChevron } from './Button'
import type { ButtonProps } from './Button'
import { itemVariants } from './Item'
import type { VariantProps } from '@joyful/utils'
import type { ComponentProps, ReactElement } from 'react'

function Select({ ...props }: ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({
  ...props
}: ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({
  ...props
}: ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

type SelectTriggerButtonProps = ButtonProps

export function SelectTriggerButton({
  align = 'between',
  children,
  endIcon,
  weight = 'normal',
  width = 'fit',
  ...props
}: SelectTriggerButtonProps) {
  return (
    <Button
      variant="outline"
      align={align}
      weight={weight}
      width={width}
      endIcon={
        <span className="text-tremor-content flex size-auto items-center gap-x-2">
          {endIcon}
          <SelectPrimitive.Icon asChild>
            <RotatingChevron className="size-3" />
          </SelectPrimitive.Icon>
        </span>
      }
      {...props}
    >
      <span className="min-w-0 flex-[1_0_0] text-start whitespace-normal">
        {children}
      </span>
    </Button>
  )
}

export interface SelectTriggerProps extends Omit<
  ComponentProps<typeof SelectPrimitive.Trigger>,
  'asChild'
> {
  buttonProps?: Omit<ButtonProps, 'children'>
}

function SelectTrigger({
  buttonProps,
  children,
  className,
  ...props
}: SelectTriggerProps) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        "data-placeholder:text-tremor-content-subtle [&_svg:not([class*='text-'])]:text-tremor-content whitespace-nowrap outline-hidden transition-[color] *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        focusRingClassName,
        className,
      )}
      asChild
      {...props}
    >
      <SelectTriggerButton {...buttonProps}>{children}</SelectTriggerButton>
    </SelectPrimitive.Trigger>
  )
}

const RawSelectTrigger = SelectPrimitive.Trigger

export type SelectContentProps = ComponentProps<typeof SelectPrimitive.Content>

function SelectContent({
  children,
  className,
  position = 'item-aligned',
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'bg-tremor-background text-tremor-content-strong data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 border-tremor-border relative z-40 max-h-(--radix-select-content-available-height) max-w-96 min-w-32 origin-(--radix-popper-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        'text-tremor-content px-2 py-1 text-xs font-medium',
        className,
      )}
      {...props}
    />
  )
}

export interface SelectItemProps
  extends
    ComponentProps<typeof SelectPrimitive.Item>,
    Omit<VariantProps<typeof itemVariants>, 'isInteractive'> {
  containerClassName?: string
  endAdornment?: ReactElement<unknown>
  labelAdornment?: ReactElement<unknown>
  startAdornment?: ReactElement<unknown>
  subtitle?: string
}

function SelectItem({
  children,
  className,
  containerClassName,
  endAdornment,
  isInset,
  labelAdornment,
  size,
  startAdornment,
  subtitle,
  ...props
}: SelectItemProps) {
  const classNames = itemVariants({
    isInset,
    isInteractive: true,
    size,
  })
  return (
    <SelectPrimitive.Item
      data-disabled={props.disabled}
      data-slot="select-item"
      className={cn(classNames.base(), className)}
      {...props}
    >
      {startAdornment}

      <div className={cn(classNames.container(), containerClassName)}>
        <SelectPrimitive.ItemText>
          {children}
          {labelAdornment}
        </SelectPrimitive.ItemText>

        {subtitle === undefined ? null : (
          <p className={classNames.subtitle()}>{subtitle}</p>
        )}
      </div>

      {endAdornment}

      <span className="flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  RawSelectTrigger,
  SelectTrigger,
  SelectValue,
}
